import {useQuery} from "@apollo/client";
import {Table, TableBody, TableCell, TableRow} from "@mui/material";
import FormattedTableHead from "../FormattedTableHead";
import {Link} from "../widgets";
import {
    SupervisedCallQueuesDocument,
    SupervisedCallQueuesQuery,
    SupervisedCallQueuesQueryVariables,
} from "./queries.generated";
import Loading from "../breadcrumbs/Loading";
import {Capability} from "@/api";
import {defineMessage, FormattedMessage} from "react-intl";

type CallQueue = SupervisedCallQueuesQuery["supervisedCallQueues"][number];

// Get the link that the user should be redirected to when clicking on the call queue
const getLink = (extId: number, cqId: number) => `/personal-extensions/${extId}/call-queues/${cqId}`;

export default ({extensionId}: {extensionId: number}) => {
    const {loading, data} = useQuery<SupervisedCallQueuesQuery, SupervisedCallQueuesQueryVariables>(
        SupervisedCallQueuesDocument,
        {
            variables: {userId: extensionId},
            fetchPolicy: "no-cache",
        },
    );

    if (loading) return <Loading />;

    if (data.userCapabilities.includes(Capability.CcSupervisor))
        return <CallQueueTable callQueues={data.supervisedCallQueues ?? []} extensionId={extensionId} />;

    return <NoSupervisorCapability />;
};

const CallQueueTable = ({callQueues, extensionId}: {callQueues: CallQueue[]; extensionId: number}) => (
    <div style={{padding: 20}}>
        <Table>
            <FormattedTableHead
                cells={[
                    {label: defineMessage({id: "name", defaultMessage: "Name"})},
                    {label: defineMessage({id: "shortNumber", defaultMessage: "Short number"})},
                ]}
            />
            <TableBody>
                {callQueues.map((cq) => (
                    <TableRow key={cq.id}>
                        <TableCell>
                            <Link to={getLink(extensionId, cq.id)}>{cq.name}</Link>
                        </TableCell>
                        <TableCell>
                            <Link to={getLink(extensionId, cq.id)}>{cq.number}</Link>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </div>
);

const NoSupervisorCapability = () => (
    <div style={{padding: 20, fontSize: "1.5rem"}}>
        <FormattedMessage
            id="personal_extensions.no_supervisor"
            defaultMessage="This user does not have supervisor capability."
        />
    </div>
);
